import React, { useEffect, useState } from 'react';
import { Box } from '@withjoy/joykit';
import type { ImageLayerData } from './Layer.types';
import { MEDIA_SERVICE } from '../steps/CardDesign/config';
import type { PageEditor } from './Page';
import { loadImageDimensions } from '@shared/components/EmailsAndEcards/components/EmailEditor/components/EmailEditPanel/components/PhotoSelector/PhotoSelector.controller';
import { EditableCroppedImageLayer, CroppedImageLayer } from './CroppedImageLayer';
import { layerLayoutToCss } from '@apps/card/utils';
import type { Rendition } from '@apps/card/utils/mediaService';
import { applyRenditionIfMediaServiceUrl } from '@apps/card/utils/mediaService';
import { isUrlWithHttpProtocol } from '@shared/utils/urlHelpers';

const MINIMUM_PPI = 150;
export const SCALE_MAX = 72 / MINIMUM_PPI; // Prevent blurry prints

// A fallback URL to use while we're waiting the media service URL to be read.
// key:   layer.imageData.src
// value: filestack image "preview" URL
export const previewUrls = new Map<string, string>();

// ImageLayerData.imageData.src is defined as one of two forms:
// a) container/asset - if event-specific
// b) asset           - if event-agnostic
// This function simply prepends the event-agonstic container ID as needed.
// Then prepends the media service URL.

// Hacking this to allow passing through URLs that are already absolute paths.
// Not a permenant addition. My assumption is that we will add the images I'm
// using to mediaservice or some other form of storage we control.

export function absolutePhotoUrl(imageLayerSrc: string) {
  if (isUrlWithHttpProtocol(imageLayerSrc)) {
    return imageLayerSrc; // Passthrough.
  }

  // Event-agnostic (slash-less) srcs are always served from production media service,
  // so in that case we do not use the MEDIA_SERVICE constant which is env-specific.
  return imageLayerSrc.includes('/') ? `${MEDIA_SERVICE}/${imageLayerSrc}` : `https://withjoy.com/media/paper/${imageLayerSrc}`;
}

function backgroundOrMaskProps({ url, mask, rendition }: { url: string; mask?: { color: string }; rendition?: Rendition }): React.CSSProperties {
  const prefix = mask ? 'mask' : 'background';

  return {
    // Images sizes served via Media Service (withjoy.com/media) vs directly from Azure Blob:
    //                          Azure     Media Service
    // Examples invitations:    Blob      Default  Medium
    //   cherie                 12.7MB    1.6MB    408KB
    //   bohemian_eucalyptus     3.1MB    556KB    119KB
    [prefix + 'Image']: `url(${applyRenditionIfMediaServiceUrl(url, rendition)})`,
    [prefix + 'Size']: 'cover',
    [prefix + 'Position']: 'center',
    [prefix + 'Repeat']: 'no-repeat',
    ...(mask ? { backgroundColor: mask.color } : {}),
  };
}

export type Size = { width: number; height: number };

// Could merge with CroppedImageLayer, but would need to wait for the image natural w/h to load before render, which is not ideal.
export const ImageLayer = (props: { layerIndex: number; layer: ImageLayerData; editor?: PageEditor; mask?: { color: string }; rendition?: Rendition }) => {
  const { layer, editor, layerIndex, mask, rendition } = props;
  const isActive = editor?.isActive(layerIndex) ?? false;

  const url = previewUrls.get(layer.imageData.src) ?? absolutePhotoUrl(layer.imageData.src);

  const [photoSize, setPhotoSize] = useState<Size>(); // Only used if $layer.editable
  useEffect(() => {
    if (!layer.editable) return;
    void (async () => setPhotoSize(await loadImageDimensions(url)))();
  }, [layer.editable, url]);

  if (layer.editable) {
    if (!photoSize) {
      return (
        <Box
          style={{
            ...layerLayoutToCss(layer.layout),
          }}
        />
      ); // Wait for the image to load
    }

    return isActive && editor ? (
      <EditableCroppedImageLayer
        {...props}
        editor={editor}
        photoSize={photoSize}
      />
    ) : (
      <CroppedImageLayer
        layer={layer}
        photoSize={photoSize}
        rendition={rendition}
      />
    );
  }

  return (
    <>
      <Box
        style={{
          ...layerLayoutToCss(layer.layout),
          overflow: 'hidden', // Safari not responding to 'clip'
          ...backgroundOrMaskProps({ url, mask, rendition }),
        }}
      />
    </>
  );
};
